import React, { useEffect } from 'react';

const SITE_URL = 'https://geostays.co';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_stayswap-3/artifacts/greklbdc_GEOSTAYS-01.jpg';
const SITE_NAME = 'GeoStays';

/**
 * SEO component — fully imperative to avoid duplicate tags from static index.html.
 * Manages: <title>, meta description/keywords/robots, canonical link,
 * Open Graph (type/site_name/title/description/url/image/locale),
 * Twitter Card (card/title/description/image), and optional JSON-LD.
 */

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const SEO = ({
  title,
  description,
  keywords,
  path = '',
  image,
  type = 'website',
  jsonLd,
  noindex = false,
}) => {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} — Sustainable Homestays, Farmstays & B&Bs in India`;
  const fullDescription =
    description ||
    'Discover unique homestays, farmstays, B&Bs, villas and boutique stays across India. Verified hosts, instant booking, and one-of-a-kind locations on GeoStays.';
  const fullKeywords =
    keywords ||
    'homestays in India, farmstays in India, villas in India, B&B India, holiday homes, weekend getaways India, sustainable stays, boutique homestays, GeoStays';
  const canonical = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.title = fullTitle;

    upsertMeta('meta[name="description"]', { name: 'description', content: fullDescription });
    upsertMeta('meta[name="keywords"]', { name: 'keywords', content: fullKeywords });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow' });
    upsertLink('canonical', canonical);

    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: fullDescription });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'en_IN' });

    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: fullDescription });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage });

    // JSON-LD: remove previous app-managed scripts then add fresh one
    document.head
      .querySelectorAll('script[type="application/ld+json"][data-seo="app"]')
      .forEach((el) => el.remove());
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.seo = 'app';
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [fullTitle, fullDescription, fullKeywords, canonical, ogImage, type, noindex, jsonLd]);

  return null;
};

export default SEO;
